import { useCallback, useEffect, useRef, useState } from "react";
import {
    Alert,
    Avatar,
    Box,
    Button,
    Chip,
    CircularProgress,
    FormControl,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Snackbar,
    Stack,
    Typography,
} from "@mui/material";
import MusicNoteIcon from "@mui/icons-material/MusicNote";
import { config } from "../Const";
import { executeFetchAsync } from "../Util";

const TEMPLATES = [
    ["WRONG_ANSWER", "Wrong answer"],
    ["FULL_PLAYBACK", "Full playback"],
    ["BEAT_THE_CROWD", "Beat the crowd"],
    ["BEAT_THE_CLOCK", "Beat the clock"],
    ["THREE_FACTS", "Three facts"],
];

export default function TikTokShort({ seoTitle, type }) {
    const [template, setTemplate] = useState("WRONG_ANSWER");
    const [connection, setConnection] = useState(null);
    const [connectionLoading, setConnectionLoading] = useState(true);
    const [loginPending, setLoginPending] = useState(false);
    const [generating, setGenerating] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [previewUrl, setPreviewUrl] = useState(null);
    const [publication, setPublication] = useState(null);
    const [message, setMessage] = useState(null);
    const popupTimer = useRef(null);

    const clearPopupTimer = useCallback(() => {
        if (popupTimer.current) {
            window.clearInterval(popupTimer.current);
            popupTimer.current = null;
        }
    }, []);

    useEffect(() => {
        setPublication(null);
    }, [seoTitle, template]);

    useEffect(() => () => clearPopupTimer(), [clearPopupTimer]);

    useEffect(() => () => {
        if (previewUrl) URL.revokeObjectURL(previewUrl);
    }, [previewUrl]);

    const loadConnection = useCallback(async () => {
        setConnectionLoading(true);
        try {
            const result = await executeFetchAsync(
                `${config.SERVER_URL}/api/v1/admin/tiktok/connection`, "get");
            setConnection(result);
        } catch (error) {
            setConnection(null);
            setMessage({ severity: "error", text: `Could not read TikTok connection: ${error.message}` });
        } finally {
            setConnectionLoading(false);
        }
    }, []);

    useEffect(() => { void loadConnection(); }, [loadConnection]);

    useEffect(() => {
        const receiveOAuth = (event) => {
            if (event.data?.type !== "quizmyday-tiktok-oauth") return;
            clearPopupTimer();
            setLoginPending(false);
            if (event.data.success) {
                setMessage({ severity: "success", text: "TikTok connected" });
                void loadConnection();
            } else {
                setMessage({ severity: "error", text: "TikTok authorization failed" });
            }
        };
        window.addEventListener("message", receiveOAuth);
        return () => window.removeEventListener("message", receiveOAuth);
    }, [clearPopupTimer, loadConnection]);

    const connectTikTok = async () => {
        const popup = window.open(
            "", "tiktokLogin", "width=620,height=760,resizable=yes,scrollbars=yes");
        if (!popup) {
            setMessage({ severity: "warning", text: "Allow popups to connect TikTok" });
            return;
        }
        clearPopupTimer();
        setLoginPending(true);
        try {
            const response = await executeFetchAsync(
                `${config.SERVER_URL}/api/v1/admin/tiktok/authorization-url`, "get");
            popup.location.replace(response.authorizationUrl);
            popupTimer.current = window.setInterval(() => {
                if (popup.closed) {
                    clearPopupTimer();
                    setLoginPending(false);
                    void loadConnection();
                }
            }, 600);
        } catch (error) {
            popup.close();
            setLoginPending(false);
            setMessage({ severity: "error", text: `Could not start TikTok login: ${error.message}` });
        }
    };

    const generatePreview = async () => {
        setGenerating(true);
        try {
            const blob = await executeFetchAsync(
                `${config.SERVER_URL}/api/v1/admin/teaser-video/generate`,
                "post", { "Content-Type": "application/json" },
                JSON.stringify({ quizSeoTitle: seoTitle, template, socialMedia: "TIKTOK" }));
            if (previewUrl) URL.revokeObjectURL(previewUrl);
            setPreviewUrl(URL.createObjectURL(blob));

            const query = new URLSearchParams({
                quizSeoTitle: seoTitle,
                template,
                socialMedia: "TIKTOK",
            });
            const generatedPublication = await executeFetchAsync(
                `${config.SERVER_URL}/api/v1/admin/teaser-video/publication?${query}`, "get");
            setPublication(generatedPublication);
            setMessage({ severity: "success", text: "TikTok preview generated" });
        } catch (error) {
            setMessage({ severity: "error", text: `Preview generation failed: ${error.message}` });
        } finally {
            setGenerating(false);
        }
    };

    const uploadToInbox = async () => {
        setUploading(true);
        try {
            const result = await executeFetchAsync(
                `${config.SERVER_URL}/api/v1/admin/teaser-video/publish`,
                "post", { "Content-Type": "application/json" },
                JSON.stringify({ quizSeoTitle: seoTitle, template, socialMedia: "TIKTOK" }));
            setPublication(result);
            setMessage({ severity: "success", text: "Video sent to your TikTok inbox" });
        } catch (error) {
            setMessage({ severity: "error", text: `TikTok inbox upload failed: ${error.message}` });
        } finally {
            setUploading(false);
        }
    };

    if (type !== "QUIZ") {
        return <Alert severity="info">Select a quiz to generate a TikTok video.</Alert>;
    }

    const delivered = publication?.status === "AWAITING_MANUAL_PUBLISH"
        || publication?.tikTokPublishingStage === "DELIVERED_TO_INBOX";

    return (
        <Stack spacing={3} sx={{ width: "100%" }}>
            <Paper variant="outlined" sx={{ p: 2 }}>
                <Stack direction={{ xs: "column", md: "row" }} spacing={2}
                       alignItems={{ md: "center" }} justifyContent="space-between">
                    <Stack direction="row" spacing={1.5} alignItems="center">
                        {connection?.avatarUrl && (
                            <Avatar src={connection.avatarUrl} alt={connection.displayName || "TikTok account"} />
                        )}
                        <Box>
                            <Typography variant="subtitle1" fontWeight={700}>TikTok account</Typography>
                            {connectionLoading ? <CircularProgress size={20} /> : connection?.connected ? (
                                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                                    <Chip color="success" size="small" label="Connected" />
                                    <Typography variant="body2">
                                        {connection.displayName || connection.openId}
                                    </Typography>
                                </Stack>
                            ) : (
                                <Typography variant="body2" color="error">
                                    {connection?.lastError || "TikTok is not connected."}
                                </Typography>
                            )}
                        </Box>
                    </Stack>
                    <Button variant={connection?.connected ? "outlined" : "contained"}
                            startIcon={loginPending ? <CircularProgress size={16} /> : <MusicNoteIcon />}
                            disabled={loginPending} onClick={connectTikTok}>
                        {connection?.connected ? "Reconnect TikTok" : "Connect TikTok"}
                    </Button>
                </Stack>
            </Paper>

            <Alert severity="info">
                The generated video is intentionally silent. After it reaches your TikTok inbox,
                open it in the TikTok app, choose a soundtrack, finish editing, and publish manually.
            </Alert>

            <Box sx={{ display: "grid",
                gridTemplateColumns: { xs: "1fr", lg: "minmax(0, 1fr) minmax(320px, .8fr)" },
                gap: 3 }}>
                <Stack spacing={2}>
                    <FormControl fullWidth>
                        <InputLabel id="tiktok-template-label">Teaser format</InputLabel>
                        <Select labelId="tiktok-template-label" value={template}
                                label="Teaser format"
                                onChange={(event) => setTemplate(event.target.value)}>
                            {TEMPLATES.map(([value, label]) => (
                                <MenuItem key={value} value={value}>{label}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                        <Button variant="outlined" disabled={generating || uploading}
                                onClick={generatePreview}
                                startIcon={generating ? <CircularProgress size={16} /> : null}>
                            Generate preview
                        </Button>
                        <Button variant="contained" disabled={uploading || generating
                                || !connection?.connected || delivered}
                                onClick={uploadToInbox}
                                startIcon={uploading ? <CircularProgress size={16} /> : <MusicNoteIcon />}>
                            {delivered ? "Sent to TikTok inbox" : "Send to TikTok inbox"}
                        </Button>
                    </Stack>

                    {publication && (
                        <Alert severity={delivered ? "success"
                                : publication.status === "FAILED" ? "error" : "info"}>
                            <Typography variant="body2">Status: {publication.status}</Typography>
                            {publication.tikTokProcessingStatus && (
                                <Typography variant="caption" display="block">
                                    TikTok: {publication.tikTokProcessingStatus}
                                </Typography>
                            )}
                            {publication.tikTokFailureReason && (
                                <Typography variant="caption" display="block">
                                    {publication.tikTokFailureReason}
                                </Typography>
                            )}
                        </Alert>
                    )}
                </Stack>

                <Paper variant="outlined" sx={{ p: 2, minHeight: 320 }}>
                    <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                        Video preview
                    </Typography>
                    {previewUrl ? (
                        <Box component="video" src={previewUrl} controls sx={{
                            width: "100%", maxHeight: 620, bgcolor: "black", borderRadius: 1,
                        }} />
                    ) : (
                        <Box sx={{ minHeight: 260, display: "grid", placeItems: "center",
                            bgcolor: "grey.100", borderRadius: 1, p: 3 }}>
                            <Typography color="text.secondary" textAlign="center">
                                Generate a preview to inspect the exact vertical video before
                                sending it to TikTok.
                            </Typography>
                        </Box>
                    )}
                </Paper>
            </Box>

            <Snackbar open={Boolean(message)} autoHideDuration={6000}
                      onClose={() => setMessage(null)}>
                {message ? (
                    <Alert severity={message.severity} onClose={() => setMessage(null)}>
                        {message.text}
                    </Alert>
                ) : undefined}
            </Snackbar>
        </Stack>
    );
}
